import { types } from "./mutations";
import { widgetType } from "@/models/Widget.model";
import { getAnalysisWidgets } from "@/api/analysis";
import cloneDeep from "lodash/cloneDeep";
import { getStaticEntities } from "@/api/static";
import { getEntityFilters } from "@/api/entities";
import { getDashboardList } from "@/api/dashboards";
import omit from "lodash/omit";

export default {
  /**
   * Sets the mode (edit/view) of the console
   * @param {Object} Context
   * @param {String} mode - "EDIT" or "VIEW"
   */
  setMode: async ({ commit }, mode) => {
    commit(types.SET_MODE, mode);
  },

  /**
   * Method to fetch all dashboards
   * Returns the dashboard list if present in the store else makes API request
   * @param {Object} Context
   */
  fetchConsoles: async ({ commit, state, getters }) => {
    // check if entities list is non empty
    if (state.consolesById && state.consolesById.length > 0)
      return {
        status: 200,
        data: {
          data: getters.getConsoles,
        },
      };

    try {
      const { data, status } = await getDashboardList("static_analysis");
      // save the dashboard list in store
      if (status === 200 && data && data.dashboards) {
        const consoles = data.dashboards.reduce((result, dashboard) => {
          result[dashboard.id] = dashboard;
          return result;
        }, {});
        commit(types.SET_CONSOLES, consoles);
        return data.dashboards;
      } else {
        commit(types.SET_CONSOLES, {});
        throw new Error("Invalid response data");
      }
    } catch (error) {
      console.error(error);
      return;
    }
  },

  /**
   * Action to initialize the console as the editor
   * @param {Object} Context
   * @param {Object} options
   * @param {String} options.consoleId - consoleId to be initialized
   * @param {String} options.mode - EDIT/MODE
   */
  initializeConsole: async (
    { commit, state, dispatch },
    { consoleId, mode }
  ) => {
    if (!consoleId) {
      // If consoleId is not present, then initialized an empty static console:
      // await dispatch("fetchStaticEntityList", {}); // Done from AnalysisParent
      commit(types.INITIALIZE_CONSOLE, { mode: "EDIT" });
      return;
    }

    // TODO: Fetch individual console if not present already:
    let staticConsole = state.consoles[consoleId];

    // If activeCity is preset, set it:
    if (staticConsole.meta.city) {
      let city = {
        id: staticConsole.meta.city.id,
        name: staticConsole.meta.city.name,
        state: staticConsole.meta.city.state,
        bbox: staticConsole.meta.city.bbox,
        latlong: staticConsole.meta.city.lat_lon,
        zoom: staticConsole.meta.city.zoom_level,
      };
      await dispatch("Map/setActiveCity", city, {
        root: true,
      });
    }

    // TODO: Once, entity_list/ starts supporting filters(city and time) add them here:
    // await dispatch("fetchStaticEntityList", {}); // Done from AnalysisParent

    // Iterate over analyses array and make an object out of it with id as key:
    let newLayers = staticConsole.analyses.reduce((result, analysis) => {
      let layer = analysis.meta;
      result[layer.id] = layer;
      return result;
    }, {});

    // Set the layers:
    commit(types.SET_LAYERS, newLayers);

    // Set the first layer as activeLayer:
    await dispatch("setActiveLayer", {
      layerId: staticConsole.analyses[0].meta.id,
    });

    // Fetch all the data:
    await dispatch("refetchAllData");

    // Mark the console initialized:
    commit(types.INITIALIZE_CONSOLE, { mode });
  },

  /**
   * Set's the static entity for the console:
   * @param {Object} Context
   * @param {String} staticEntityId
   */
  setStaticEntityId: async ({ commit }, staticEntityId) => {
    commit(types.SET_STATIC_ENTITY_ID, staticEntityId);
  },

  /**
   * Set static entity filters
   * @param {Object} Context
   * @param {Object} filter
   */
  // setStaticEntityFilters: async ({ commit, state, dispatch }, filter) => {
  //   let newFilters = [...state.staticEntityFilters];
  //   let index = newFilters.find(f => f.id === filter.id);
  //   if (index === -1) newFilters.push(filter);
  //   else newFilters.splice(index, 1);

  //   commit(types.SET_STATIC_ENTITY_FILTERS, newFilters);
  //   // TODO: Fetch data with the filter. @haxie
  //   // await dispatch("refetchAllData");
  // },

  fetchStaticEntityFilters: async ({ commit, state }) => {
    // console.log("reached fetch static entity");
    if (!state.staticEntityId) return;
    // check if the filters already exists
    if (state.staticEntityFilters[state.staticEntityId]) return;
    try {
      const response = await getEntityFilters(state.staticEntityId);
      const { status, data } = response;
      if (status === 200 && data) {
        commit(types.SET_STATIC_ENTITY_FILTERS, {
          [state.staticEntityId]: {
            ...data.data,
          },
        });
      } else {
        throw new Error("Invalid response");
      }
    } catch (error) {
      console.error(error);
      return;
    }
  },

  toggleEntityFilter: async ({ commit, state }, { column, value }) => {
    const _appliedFilters = cloneDeep(state.appliedFilters);
    // check if the column is present in the filter object
    if (_appliedFilters[column]) {
      // check if the filter is already applied
      if (_appliedFilters[column].includes(value)) {
        // if yes, remove the filter
        _appliedFilters[column] = _appliedFilters[column].filter(
          item => item !== value
        );
      } else {
        // filter, doesn't exist, then add the filter
        _appliedFilters[column].push(value);
      }
    } else {
      // if the column entry is not present, then simply add a new entry
      _appliedFilters[column] = [value];
    }

    commit(types.SET_APPLIED_FILTERS, _appliedFilters);
  },

  /**
   * Add a new Layer and make it active:
   * @param {Object} Context
   * @param {Object} layer
   */
  addLayer: async ({ commit, state, dispatch }, layer) => {
    commit(types.SET_LAYERS, {
      ...state.layers,
      [layer.id]: layer,
    });
    dispatch("setActiveLayer", { layerId: layer.id, fetchWidgets: true });
  },

  /**
   * Updates an existing layer in the layersList and make it active
   * @param {Object} Context
   * @param {Object} layer
   */
  updateLayer: async ({ commit, state, dispatch }, layer) => {
    commit(types.SET_LAYERS, {
      ...state.layers,
      [layer.id]: layer,
    });
    dispatch("setActiveLayer", { layerId: layer.id, fetchWidgets: true });
  },

  /**
   * Removes an existing layer from the layerList
   * @param {Object} Context
   * @param {String} layerIdToDelete id of the layer to be removed
   */
  removeLayer: async ({ commit, state, dispatch }, layerIdToDelete) => {
    // Remove Layer:
    let newLayers = {};
    state.layersById.forEach(layerId => {
      if (layerId !== layerIdToDelete) {
        newLayers[layerId] = state.layers[layerId];
      }
    });

    // Remove geo_static data from staticEntities:
    let newStaticEntities = state.staticEntities.map(entity => {
      return {
        ...entity,
        analyses: omit(entity.analyses, layerIdToDelete),
      };
    });

    // Remove timeValues:
    let newTimeValues = omit(state.timeValues, layerIdToDelete);

    // Remove detailsWidgets:
    let newDetailsWidgets = omit(state.detailsWidgets, layerIdToDelete);

    // Remove distributionWidgets:
    let newDistributionWidgets = omit(
      state.distributionWidgets,
      layerIdToDelete
    );

    commit(types.SET_LAYERS, newLayers);
    commit(types.SET_STATIC_ENTITIES, newStaticEntities);
    commit(types.SET_TIME_VALUES, newTimeValues);
    commit(types.SET_DETAILS_WIDGETS, newDetailsWidgets);
    commit(types.SET_DISTRIBUTION_WIDGETS, newDistributionWidgets);

    // If layer to be deleted is an active layer:
    if (state.activeLayer === layerIdToDelete) {
      // Set new active layer:
      let newLayersId = Object.keys(newLayers);
      if (newLayersId.length > 0) {
        // Set the first layer as active:
        dispatch("setActiveLayer", { layerId: newLayersId[0] });
      } else dispatch("setActiveLayer", { layerId: null });
    }
  },

  /**
   * Sets a layer in the layerList as an activeLayer
   * @param {Object} Context
   * @param {Object} options
   * @param {String} options.layerId
   * @param {Boolean} options.fetchWidgets - If set to true, then fetch the widget values
   */
  setActiveLayer: async (
    { commit, state, dispatch },
    { layerId, fetchWidgets = false }
  ) => {
    commit(types.SET_ACTIVE_LAYER, layerId);

    // If layerId is null, just return:
    // if (!layerId) return;

    if (fetchWidgets) {
      commit(types.SET_REQUEST_PROGRESS, true);

      // TODO: Stop relying on fetchWidgets and just check if data is present or not, and fetch if not present:
      // if (!(state.layers[layerId].value && state.layers[layerId].timeValues)) {
      // Fetch Widgets only if asked for: (new/update layer)
      // If single data is already present don't ask for it:
      // let options = {};
      // if (state.layers[layerId].value) {
      //   options.payloadType = "REMAINING_WIDGETS";
      // }
      // const payload = await dispatch("createPayload", options);

      let requests = [];
      const payload = await dispatch("createPayload", {});
      requests.push(dispatch("fetchWidgets", { payload }));
      requests.push(dispatch("fetchDetailsWidget", { layerId }));
      requests.push(dispatch("fetchDistributionWidget", { layerId }));

      Promise.all(requests).then(values => {
        // Once all the requests have been completed, mark request progress complete:
        commit(types.SET_REQUEST_PROGRESS, false);
      });
    } else {
      // TODO: Need to cache the details also.

      // If we are not fetchingWidgets, then just update the Timemanager with the cached timeValues of currentLayer
      if (layerId && state.timeValues[layerId])
        // If timeValues for current layerId are present update the TimeManager store:
        dispatch("TimeManager/loadTimeData", state.timeValues[layerId], {
          root: true,
        });
      else {
        // Else reset the store
        // console.log("SETTING TIMEMANGE");
        dispatch("TimeManager/loadTimeData", null, {
          root: true,
        });
      }
    }
  },

  /**
   * Refect all the widgets data
   * @param {Object} Context
   */
  refetchAllData: async ({ state, dispatch }) => {
    // Fetch all the widgets for activeLayer:
    let payload = await dispatch("createPayload", {});

    if (payload) {
      dispatch("fetchWidgets", { payload, clearAllRequests: true });
    }

    // Make a request for all the remaining layers:
    for (let i = 0; i < state.layersById.length; i++) {
      let layerId = state.layersById[i];
      dispatch("fetchDetailsWidget", { layerId });
      dispatch("fetchDistributionWidget", { layerId });

      if (layerId === state.activeLayer) continue;

      let payload = await dispatch("createPayload", {
        // payloadType: "SINGLE_AND_GEO", // Not doing this for now.
        layerId: layerId,
      });

      if (payload) {
        dispatch("fetchWidgets", { payload, layerId });
      }
    }
  },

  /**
   * Fetch Static Entities
   * @param {Object} Context
   * @param {Object} options
   * @param {Boolean} options.scaffoldImmediately - If set to true, update the store immediately
   */
  fetchStaticEntityList: async (
    { commit, state, dispatch },
    { scaffoldImmediately = true }
  ) => {
    // commit(types.SET_REQUEST_PROGRESS, true); // It's never going to be fetched alone

    try {
      let response = await getStaticEntities({
        entity_id: state.staticEntityId,
      });

      if (response.status === 200 && response.data) {
        // If we want to fetch the analysis data also, then await fetchStaticEntityList and then dispatch scaffold from setActiveCity,
        if (scaffoldImmediately)
          await dispatch("scaffoldStaticEntities", response.data.data);

        return response.data;
      } else {
        throw new Error("Invalid response data");
      }
    } catch (error) {
      console.error(error);
      return;
    }
  },

  /**
   * Creates a payload for the data present in the store
   * @param {Object} Context
   */
  createPayloadForSaving: async ({ state, dispatch, commit, rootGetters }) => {
    if (!(state.layersById.length > 0)) return;

    // commit(types.SET_REQUEST_PROGRESS, true);

    let analyses = [];

    // Loop over the layers:
    state.layersById.forEach(layerId => {
      // Omit the single value (widget):
      let Layer = omit(state.layers[layerId], "value");

      let analysis_type,
        expr_terms = [],
        expr_str;

      // Depending on the Layer.type, add the options:
      switch (Layer.type) {
        case "EVENT_TOTALS": {
          analysis_type = Layer.radius ? "event_within_radius" : "event_count";
          expr_terms = [
            {
              name: "A",
              entity_id: Layer.entity.id,
              event_column: Layer.event.event_col,
              event_name: Layer.event.event_name,
            },
          ];
          if (Layer.radius) {
            expr_terms[0].event_radius = Layer.radius;
          }
          expr_str = "A";
          break;
        }
        case "TIME_SPENT": {
          analysis_type = "time_delta";
          break;
        }
      }

      analyses.push({
        meta: Layer,
        analysis_type,
        base_entity_id: state.staticEntityId,
        expr_terms,
        expr_str,
      });
    });

    let meta = {};
    let cityName = "unknown";
    let activeCity = rootGetters["Map/getActiveCity"];

    if (
      activeCity &&
      Object.entries(activeCity).length !== 0
      // && activeCity.constructor === Object
    ) {
      meta.city = {
        // ...activeCity,
        id: activeCity.id,
        name: activeCity.name,
        state: "null", // TODO:  Add proper state name
        bbox: activeCity.bbox,
        lat_lon: activeCity.latlong,
        zoom_level: activeCity.zoom,
      };
      cityName = activeCity.name;
    }

    return {
      name: "static_analysis_name",
      description: "static_analysis_description",
      map_style: rootGetters["Map/getMapStyle"],
      city: cityName,
      meta,
      analysis_type: "static_analysis",
      analyses,
    };
  },

  /**
   * Creates a payload for the data present in the store
   * @param {Object} Context
   * @param {Object} options
   * @param {String} payloadType
   * @param {String} [layerId=activeLayer]
   */
  createPayload: async (
    { state, dispatch, commit },
    { payloadType, layerId = state.activeLayer }
  ) => {
    if (!layerId) return;

    commit(types.SET_REQUEST_PROGRESS, true);

    const time_filters = await dispatch(
      "TimeManager/createTimeFilterpayload",
      null,
      {
        root: true,
      }
    );
    const geo_filters = [];

    const city_filter = await dispatch("Map/getCityFilterPayload", null, {
      root: true,
    });

    // If city_filter is non-empty object add it to geo_filters:
    if (
      city_filter &&
      Object.entries(city_filter).length !== 0 &&
      city_filter.constructor === Object
    ) {
      geo_filters.push(city_filter);
    }

    const Layer = state.layers[layerId];

    // Options:
    let widget_types = [],
      analysis_type,
      expr_terms = [],
      expr_str;

    switch (payloadType) {
      // case "ALL_SINGLES": {
      //   widget_types = [{ type: "single" }];
      //   break;
      // }
      // case "REMAINING_WIDGETS": {
      //   widget_types = [
      //     { type: "time" },
      //     { type: "geo_static" },
      //     // { type: "details" },
      //   ];
      //   break;
      // }
      case "DETAILS_WIDGET":
        widget_types = [{ type: "details" }];
        break;
      case "DISTRIBUTION_WIDGET":
        widget_types = [{ type: "distribution" }];
        break;
      default: {
        widget_types = [
          { type: "time" },
          { type: "single" },
          { type: "geo_static" },
          // { type: "details" },
        ];
      }
    }

    // Depending on the Layer.type, add the options:
    switch (Layer.type) {
      case "EVENT_TOTALS": {
        switch (payloadType) {
          case "DETAILS_WIDGET":
            analysis_type = Layer.radius
              ? "event_within_radius_delta"
              : "event_count_delta";
            break;
          case "DISTRIBUTION_WIDGET":
            analysis_type = Layer.radius
              ? "event_within_radius_distribution"
              : "event_count_distribution";
            break;
          default:
            analysis_type = Layer.radius
              ? "event_within_radius"
              : "event_count";
            break;
        }
        expr_terms = [
          {
            name: "A",
            entity_id: Layer.entity.id,
            event_column: Layer.event.event_col,
            event_name: Layer.event.event_name,
          },
        ];
        if (Layer.radius) {
          expr_terms[0].event_radius = Layer.radius;
        }
        expr_str = "A";
        break;
      }
      case "TIME_SPENT": {
        analysis_type = "time_delta";
        break;
      }
      default:
    }

    return {
      widget_types: widget_types,
      params: [
        {
          meta: Layer,
          analysis_type,
          base_entity_id: state.staticEntityId || "staticEntityId Not Set",
          expr_terms,
          expr_str,
        },
      ],
      geo_filters,
      time_filters,
    };
  },

  /**
   * TODO: This is a monkey patch over the current widget request to accomodate details widget
   */
  fetchDetailsWidget: async ({ state, dispatch }, { layerId }) => {
    try {
      // console.log("fetching details...", { layerId });
      const payload = await dispatch("createPayload", {
        payloadType: "DETAILS_WIDGET",
        layerId,
      });
      dispatch("fetchWidgets", { payload });
    } catch (error) {
      console.error(error);
    }
  },

  /**
   * Fetches the distribution widget
   */
  fetchDistributionWidget: async ({ state, dispatch }, { layerId }) => {
    try {
      const payload = await dispatch("createPayload", {
        payloadType: "DISTRIBUTION_WIDGET",
        layerId,
      });
      dispatch("fetchWidgets", { payload });
    } catch (error) {
      console.error(error);
    }
  },

  /**
   * Fetchs data for the console
   * @param {Object} Context
   * @param {Object} options
   * @param {Object} options.payload
   */
  fetchWidgets: async ({ dispatch }, { payload, clearAllRequests = false }) => {
    try {
      const widgets = await dispatch(
        "Data/fetchAnalysisWidgets",
        { payload },
        { root: true }
      );
      // console.log({ widgets });
      dispatch("loadWidgetData", widgets);
    } catch (error) {
      console.log(error);
      return;
    }
  },

  /**
   * Breaks down the widget response and hands over to appropriate widgetLoaders:
   * @param {Object} Context
   * @param {Array} widgets
   */
  loadWidgetData: async ({ dispatch }, widgets) => {
    // If widgets/ request fails, widgets will be empty
    if (!(widgets && Object.keys(widgets).length > 0)) return;

    Object.keys(widgets).forEach(widget_type => {
      // If widget_type is not present we cannot handle it:
      if (!widget_type) return;

      switch (widget_type) {
        case widgetType.GEO_STATIC:
          if (
            widgets[widgetType.GEO_STATIC] &&
            widgets[widgetType.GEO_STATIC].length > 0
          ) {
            dispatch(
              "loadIndividualSingleValues",
              widgets[widgetType.GEO_STATIC]
            );
          }
          break;
        case widgetType.TIME:
          if (widgets[widgetType.TIME] && widgets[widgetType.TIME].length > 0) {
            dispatch("loadTimeValues", widgets[widgetType.TIME]);
          }
          break;
        case widgetType.SINGLE:
          if (
            widgets[widgetType.SINGLE] &&
            widgets[widgetType.SINGLE].length > 0
          ) {
            dispatch("loadSingleValues", widgets[widgetType.SINGLE]);
          }
          break;
        case widgetType.DETAILS:
          if (
            widgets[widgetType.DETAILS] &&
            widgets[widgetType.DETAILS].length > 0
          ) {
            dispatch("loadDetailsValues", widgets[widgetType.DETAILS]);
          }
          break;
        case widgetType.DISTRIBUTION:
          if (
            widgets[widgetType.DISTRIBUTION] &&
            widgets[widgetType.DISTRIBUTION].length > 0
          ) {
            dispatch(
              "loadDistributionValues",
              widgets[widgetType.DISTRIBUTION]
            );
          }
          break;
      }
    });
  },

  /**
   * Scaffolds the staticEntities once at the time of Initializing the Studio,
   * using the response from /entity_lookup on static entity
   */
  scaffoldStaticEntities: async ({ commit }, staticEntities) => {
    // check if the data is valid or not empty
    if (!(staticEntities && staticEntities.length > 0)) return;

    let tempStaticEntities = staticEntities.map(entity => {
      let processedMeta = {};
      Object.keys(entity.meta).forEach(key => {
        // If meta of static entity is null show "-" in geo popup and table:
        // processedMeta[key] = entity.meta[key] === null ? "-" : entity.meta[key];
        processedMeta[key] = entity.meta[key];
      });

      return {
        ...entity,
        meta: processedMeta,
      };
    });

    commit(types.SET_STATIC_ENTITIES, tempStaticEntities);
  },

  /**
   * Loads Single Value widgets
   * @param {Object} Context
   * @param {Array} widgetData - Array of all single value widgets
   */
  loadSingleValues: async ({ commit, state }, widgetData) => {
    // check if the data is valid or not empty
    if (!(widgetData && widgetData.length > 0)) return;

    let newLayers = { ...state.layers };
    widgetData.forEach(widget => {
      let layerId = widget.meta.id;
      // Add data to the store, if the layer is present in store:
      if (state.layersById.includes(layerId)) {
        newLayers[layerId] = {
          ...state.layers[layerId],
          value: widget.data, // Data from actual API (old structure)
          // value: widget.data[0], // Data from actual API
          // value: ~~(Math.random() * 1000), // Random data for testing
        };
      }
    });

    commit(types.SET_LAYERS, newLayers);
  },

  /**
   * Loads Time Value widgets
   * @param {Object} Context
   * @param {Array} widgetData - Array of all time value widgets
   */
  loadTimeValues: async ({ commit, state, dispatch }, widgetData) => {
    // check if the data is valid or not empty
    if (!(widgetData && widgetData.length > 0)) return;

    let newTimeValues = { ...state.timeValues };
    let activeLayerTimeData;
    widgetData.forEach(widget => {
      // console.log("Timeeee", widget);
      let layerId = widget.meta.id;

      // Add data to the store, if the layer is present in store:
      if (state.layersById.includes(layerId)) {
        newTimeValues[layerId] = [widget];

        if (layerId === state.activeLayer) {
          // console.log("Active Layer");
          activeLayerTimeData = [widget];
        }
      }
    });
    if (activeLayerTimeData && activeLayerTimeData.length > 0) {
      // console.log("activeLayerTimeData:", activeLayerTimeData);
      await dispatch("TimeManager/loadTimeData", activeLayerTimeData, {
        root: true,
      });
    }

    commit(types.SET_TIME_VALUES, newTimeValues);
  },

  /**
   * Loads Geo Static Value widgets
   * @param {Object} Context
   * @param {Array} widgetData - Array of all geo static widgets
   */
  loadIndividualSingleValues: async ({ commit, state }, widgetData) => {
    // check if the data is valid or not empty
    if (!(widgetData && widgetData.length > 0)) return;

    let newStaticEntities = cloneDeep(state.staticEntities);

    widgetData.forEach(widget => {
      let layerId = widget.meta.id;

      let minValue = Number.MAX_SAFE_INTEGER,
        maxValue = Number.MIN_SAFE_INTEGER;

      Object.values(widget.data[0]).forEach(value => {
        if (value < minValue) minValue = value;
        if (value > maxValue) maxValue = value;
      });

      let diff = maxValue - minValue;

      // Add data to the store, if the layer is present in store:
      if (state.layersById.includes(layerId)) {
        newStaticEntities = newStaticEntities.map(entity => {
          // console.log("entity::", entity.id);
          // console.log("data::", widget.data);
          return {
            ...entity,
            analyses: {
              ...entity.analyses,
              [layerId]: {
                // If widget.data[entity.id] is null add "-":
                data: widget.data[0][entity.id] || "-", // Now I had to add [0]
                // data: widget.data[entity.id] || "-", // Earlier this used to work
                // data: Math.floor(Math.random() * 100), // Random Data for testing
                normalizedData:
                  ((widget.data[0][entity.id] - minValue) / diff) * 100,
              },
            },
          };
        });
      }
    });
    commit(types.SET_STATIC_ENTITIES, newStaticEntities);
  },

  loadDetailsValues: async ({ commit, state }, widgetData) => {
    if (!(widgetData && widgetData.length > 0)) return;
    const _detailsWidgets = cloneDeep(state.detailsWidgets);

    widgetData.forEach(widget => {
      // return if the widget doesn't have an id
      if (!(widget.meta && widget.meta.id)) return;
      const widget_data = widget.data.reduce((result, item) => {
        return Object.assign(result, item);
      }, {});
      widget.data = {
        event_count: {
          current: widget_data["current_event_count"],
          previous: widget_data["previous_event_count"],
        },
        entities_count: {
          current: widget_data["current_entities_count"],
          previous: widget_data["previous_entities_count"],
        },
        sessions_count: {
          current: widget_data["current_sessions_count"],
          previous: widget_data["previous_entities_count"],
        },
      };
      // add the widget to the ovject
      _detailsWidgets[widget.meta.id] = widget;
    });
    commit(types.SET_DETAILS_WIDGETS, _detailsWidgets);
  },

  loadDistributionValues: async ({ state, commit }, widgetData) => {
    if (!(widgetData && widgetData.length > 0)) return;
    const _distributionWidgets = cloneDeep(state.distributionWidgets);

    widgetData.forEach(widget => {
      if (!(widget.meta && widget.meta.id)) return;
      const daily = widget.data[1];
      const weekly = widget.data[0];
      _distributionWidgets[widget.meta.id] = { daily, weekly };
    });
    commit(types.SET_DISTRIBUTION_WIDGETS, _distributionWidgets);
  },
  /**
   * resets the store state
   * @param {Object} Context
   */
  reset: async ({ commit, dispatch }) => {
    commit(types.reset);
    await dispatch("Map/reset", null, {
      root: true,
    });
    await dispatch("TimeManager/reset", null, {
      root: true,
    });
  },
};
